import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DeleteOutlined, EditOutlined, FileTextOutlined, FolderOpenOutlined, FolderOutlined, InboxOutlined,
  PlusOutlined, RollbackOutlined, SearchOutlined,
} from '@ant-design/icons';

import { MenuThaoTac } from '../../../components/shared';
import type { MucThaoTac } from '../../../components/shared';
import { Badge, Button, Empty, Input, Skeleton } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { TRANG_THAI_BO } from '../constants';
import type { NutBoCay, NutLinhVucCay, NutLoaiCay, TrangThaiBo } from '../types';

/**
 * Một nút của cây kèm id cha. Form sửa cần id cha (`linh_vuc_id`, `loai_ho_so_id`) mà nút cây không mang,
 * nên cây gửi kèm thay vì bắt view tra lại danh mục.
 */
export type MucCay =
  | { cap: 'linh-vuc'; nut: NutLinhVucCay }
  | { cap: 'loai'; nut: NutLoaiCay; linhVucId: string }
  | { cap: 'bo'; nut: NutBoCay; loaiId: string };

export interface QuyenCay {
  danhMucSua: boolean;
  danhMucXoa: boolean;
  boSua: boolean;
  boXoa: boolean;
}

export interface CayThuVienProps {
  cay: NutLinhVucCay[];
  dangTai: boolean;
  /** Id bộ tiêu chí đang mở ở cột giữa. */
  dangChon?: string;
  onChonBo: (boId: string) => void;
  onThemLinhVuc: () => void;
  /** Ẩn nút thêm khi không đủ quyền (ADR-UI-0005). */
  duocThem: boolean;
  /** Quyền quyết định mục nào có trong menu `⋮` của từng dòng; thiếu quyền thì mục bị ẩn. */
  quyen: QuyenCay;
  onSua: (muc: MucCay) => void;
  onXoa: (muc: MucCay) => void;
  onDoiTrangThaiBo: (bo: NutBoCay, trangThai: TrangThaiBo) => void;
}

/**
 * Cột trái của Thư viện tiêu chí: cây Lĩnh vực ▸ Loại hồ sơ ▸ Bộ tiêu chí, theo giao diện v3.
 *
 * Một lượt gọi `GET /bo-tieu-chi/cay` dựng cả cây (đặc tả mục 13.3) — không gọi ba lần rồi ghép ở client,
 * vì ba lời gọi có thể chụp ba thời điểm khác nhau và cây hiện ra không khớp với chính nó.
 *
 * Ô tìm lọc **tại chỗ** trên cây đã tải, không gọi lại API: cả thư viện của một tenant là vài chục dòng,
 * gọi lại mỗi lần gõ chỉ làm cây nhấp nháy. Nhánh nào có con khớp thì tự mở.
 *
 * Mỗi dòng có menu `⋮` (ADR-UI-0005) cho Sửa / Xoá, riêng bộ tiêu chí thêm Lưu trữ / Dùng lại.
 */
export function CayThuVien({
  cay, dangTai, dangChon, onChonBo, onThemLinhVuc, duocThem, quyen, onSua, onXoa, onDoiTrangThaiBo,
}: CayThuVienProps) {
  const { t } = useT();
  const [tim, setTim] = useState('');
  const [dong, setDong] = useState<Set<string>>(() => new Set());

  const khop = tim.trim().toLowerCase();
  const loc = useMemo(() => {
    if (!khop) return cay;
    const co = (s: string) => s.toLowerCase().includes(khop);
    return cay
      .map((lv) => ({
        ...lv,
        loai_ho_so: lv.loai_ho_so
          .map((l) => ({ ...l, bo_tieu_chi: l.bo_tieu_chi.filter((b) => co(b.ten) || co(l.ten) || co(lv.ten)) }))
          .filter((l) => l.bo_tieu_chi.length > 0 || co(l.ten) || co(lv.ten)),
      }))
      .filter((lv) => lv.loai_ho_so.length > 0 || co(lv.ten) || co(lv.ma));
  }, [cay, khop]);

  // Số con đếm trên cây **chưa lọc**: đang tìm thì `loc` đã bớt con, đếm trên đó sẽ mở khoá Xoá cho một
  // lĩnh vực vẫn còn loại hồ sơ.
  const soCon = useMemo(() => {
    const m = new Map<string, number>();
    for (const lv of cay) {
      m.set(lv.id, lv.loai_ho_so.length);
      for (const l of lv.loai_ho_so) m.set(l.id, l.bo_tieu_chi.length);
    }
    return m;
  }, [cay]);

  // Đang tìm thì mở hết: gập lại đúng nhánh người dùng vừa tìm ra là bắt họ bấm thêm một lần vô ích.
  const moNhanh = (id: string) => Boolean(khop) || !dong.has(id);
  const bat = (id: string) =>
    setDong((cu) => {
      const moi = new Set(cu);
      if (moi.has(id)) moi.delete(id);
      else moi.add(id);
      return moi;
    });

  function mucDanhMuc(muc: MucCay & { cap: 'linh-vuc' | 'loai' }): MucThaoTac[] {
    const so = soCon.get(muc.nut.id) ?? 0;
    const laLinhVuc = muc.cap === 'linh-vuc';
    return [
      {
        khoa: 'sua',
        nhan: t(laLinhVuc ? 'thamDinh.linhVuc.sua' : 'thamDinh.loaiHoSo.sua'),
        icon: <EditOutlined />,
        hien: quyen.danhMucSua,
        onChon: () => onSua(muc),
      },
      {
        khoa: 'xoa',
        nhan: t(laLinhVuc ? 'thamDinh.linhVuc.xoa' : 'thamDinh.loaiHoSo.xoa'),
        icon: <DeleteOutlined />,
        nhom: 'pha-huy',
        hien: quyen.danhMucXoa,
        // Có quyền nhưng còn con thì backend trả 409 — nói lý do ngay trong menu thay vì để người dùng
        // bấm qua hộp xác nhận rồi mới biết.
        voHieu: so > 0,
        moTa: so > 0 ? t(laLinhVuc ? 'thamDinh.cay.conLoaiHoSo' : 'thamDinh.cay.conBo', { so }) : undefined,
        onChon: () => onXoa(muc),
      },
    ];
  }

  function mucBo(b: NutBoCay, loaiId: string): MucThaoTac[] {
    const daPhatHanh = b.phien_ban_hien_tai !== null;
    return [
      {
        khoa: 'sua',
        nhan: t('thamDinh.bo.sua'),
        icon: <EditOutlined />,
        hien: quyen.boSua,
        onChon: () => onSua({ cap: 'bo', nut: b, loaiId }),
      },
      {
        khoa: 'luu-tru',
        nhan: t('thamDinh.bo.luuTru'),
        icon: <InboxOutlined />,
        hien: quyen.boSua && b.trang_thai === 'hoat_dong',
        onChon: () => onDoiTrangThaiBo(b, 'luu_tru'),
      },
      {
        khoa: 'dung-lai',
        nhan: t('thamDinh.bo.dungLai'),
        icon: <RollbackOutlined />,
        hien: quyen.boSua && b.trang_thai === 'luu_tru',
        onChon: () => onDoiTrangThaiBo(b, 'hoat_dong'),
      },
      {
        khoa: 'xoa',
        nhan: t('thamDinh.bo.xoa'),
        icon: <DeleteOutlined />,
        nhom: 'pha-huy',
        hien: quyen.boXoa,
        voHieu: daPhatHanh,
        moTa: daPhatHanh ? t('thamDinh.cay.daPhatHanh') : undefined,
        onChon: () => onXoa({ cap: 'bo', nut: b, loaiId }),
      },
    ];
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
      <div className="border-b border-default p-3">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-content-muted" />}
          placeholder={t('thamDinh.cay.tim')}
          aria-label={t('thamDinh.cay.tim')}
          value={tim}
          onChange={(e) => setTim(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {dangTai ? (
          <div className="space-y-2 p-1" aria-busy="true">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-5/6" />
            <Skeleton className="h-7 w-2/3" />
          </div>
        ) : loc.length === 0 ? (
          <Empty
            icon={FolderOpenOutlined}
            title={khop ? t('thamDinh.cay.rongLoc') : t('thamDinh.cay.rong')}
            description={khop ? undefined : t('thamDinh.cay.rongMo')}
          />
        ) : (
          <ul className="space-y-0.5" role="tree" aria-label={t('thamDinh.cay.nhan')}>
            {loc.map((lv) => (
              <li key={lv.id} role="none">
                <DongCay ten={lv.ten} muc={mucDanhMuc({ cap: 'linh-vuc', nut: lv })}>
                  <button
                    type="button"
                    aria-expanded={moNhanh(lv.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-start text-sm font-medium text-content-primary"
                    onClick={() => bat(lv.id)}
                  >
                    {moNhanh(lv.id) ? <FolderOpenOutlined className="shrink-0 text-content-muted" /> : <FolderOutlined className="shrink-0 text-content-muted" />}
                    <span className="min-w-0 flex-1 truncate">{lv.ten}</span>
                  </button>
                </DongCay>

                {moNhanh(lv.id) && (
                  <ul className="ms-3 space-y-0.5 border-s border-default ps-2" role="group">
                    {lv.loai_ho_so.map((l) => (
                      <li key={l.id} role="none">
                        <DongCay ten={l.ten} muc={mucDanhMuc({ cap: 'loai', nut: l, linhVucId: lv.id })}>
                          <button
                            type="button"
                            aria-expanded={moNhanh(l.id)}
                            className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-start text-sm text-content-secondary"
                            onClick={() => bat(l.id)}
                          >
                            {moNhanh(l.id) ? <FolderOpenOutlined className="shrink-0 text-content-muted" /> : <FolderOutlined className="shrink-0 text-content-muted" />}
                            <span className="min-w-0 flex-1 truncate">{l.ten}</span>
                          </button>
                        </DongCay>

                        {moNhanh(l.id) && (
                          <ul className="ms-3 space-y-0.5 border-s border-default ps-2" role="group">
                            {l.bo_tieu_chi.map((b) => (
                              <li key={b.id} role="none">
                                <DongCay ten={b.ten} muc={mucBo(b, l.id)} dangChon={b.id === dangChon}>
                                  <button
                                    type="button"
                                    aria-current={b.id === dangChon ? 'true' : undefined}
                                    className={cn(
                                      'flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-start text-sm',
                                      b.id === dangChon ? 'font-medium text-primary-700' : 'text-content-secondary',
                                    )}
                                    onClick={() => onChonBo(b.id)}
                                  >
                                    <FileTextOutlined className="shrink-0 text-content-muted" />
                                    <span className="min-w-0 flex-1 truncate">{b.ten}</span>
                                    {/* Lưu trữ thắng số bản: bộ đã thôi dùng thì đó là điều người soạn cần
                                        thấy trước. Còn lại: chưa phát hành là "Nháp", đã phát hành là số bản. */}
                                    {b.trang_thai === 'luu_tru' ? (
                                      <Badge variant={TRANG_THAI_BO.luu_tru.variant}>{t(TRANG_THAI_BO.luu_tru.khoa)}</Badge>
                                    ) : (
                                      <Badge variant={b.phien_ban_hien_tai === null ? 'neutral' : 'success'}>
                                        {b.phien_ban_hien_tai === null
                                          ? t('thamDinh.cay.nhap')
                                          : `v${b.phien_ban_hien_tai}`}
                                      </Badge>
                                    )}
                                  </button>
                                </DongCay>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {duocThem && (
        <div className="border-t border-default p-2">
          <Button variant="ghost" size="sm" className="w-full" icon={<PlusOutlined />} onClick={onThemLinhVuc}>
            {t('thamDinh.cay.themLinhVuc')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Một dòng của cây: nút chính và menu `⋮` đứng **cạnh nhau**, không lồng — `<button>` trong `<button>` là
 * HTML sai và trình đọc màn hình đọc gộp hai nhãn làm một.
 *
 * Menu chỉ hiện khi rê chuột hoặc focus vào dòng (và luôn hiện ở dòng đang chọn), để cây vài chục dòng không
 * thành một cột dấu `⋮`. Màn cảm ứng không có hover nên luôn hiện.
 */
function DongCay({
  ten, muc, dangChon, children,
}: { ten: string; muc: MucThaoTac[]; dangChon?: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        'group flex items-center rounded hover:bg-subtle',
        dangChon && 'bg-primary-50 hover:bg-primary-50',
      )}
    >
      {children}
      <span
        className={cn(
          'shrink-0 pe-1 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100',
          dangChon && 'opacity-100',
        )}
      >
        <MenuThaoTac muc={muc} tenBanGhi={ten} />
      </span>
    </div>
  );
}
